var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { systemStatuses } from '../../../../backend/features/accounts/shared';
import { requireAnySystemPermission } from '../../../../backend/features/permissions/shared';
import { getOrganizationMap } from '../../../../ui/features/organizations/utils/getOrganizationMap';
import { useUserState } from '../../../../ui/hooks';
import { useContactManagementTranslation } from '../../../../ui/hooks/useContactManagementTranslation';
import { useFilterConfig } from '@juvare/common-data-table-ui/FiltersBar';
import { getConfig } from '@juvare/common-web-ui/config';
import { buildDynamicFilters } from '@juvare/platform-sdk-react/ui/features/dynamic-columns';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useSelector } from 'react-redux';
export function useAccountFilterConfig({ requireOrgRoleInStatusFilter, extendFilters, showAccountStatusFilter, showAccountSystemRoleFilter, staticOrganizationId, staticOrganizationName, showOrganizationStatusFilter, showOrganizationRoleFilter, advancedSearchItems, }) {
    if ((showOrganizationStatusFilter || showOrganizationRoleFilter) && !staticOrganizationName) {
        console.error('Trying to use organization filters without staticOrganizationId');
    }
    const userState = useUserState();
    const { t } = useContactManagementTranslation();
    const { systemRoleMap, orgStatusMap, orgRoleMap, useConsentlessTexting } = useSelector(getSettings);
    const { contactManagement } = getConfig();
    const filteredSystemRoleMap = Object.assign({}, systemRoleMap);
    if (!requireAnySystemPermission(userState, 'view-system-admin-accounts')) {
        delete filteredSystemRoleMap['system-admin'];
    }
    return useFilterConfig((builder) => {
        const advancedSearchFilter = builder.quickAdvancedSearchFilter({
            search: {
                dataPath: 'partialSearch',
                label: t('common.search'),
                sx: { maxWidth: { xs: undefined, sm: 340 } },
                dataTest: 'search-input',
            },
            advancedSearch: {
                dataPath: 'advanced',
                field: {
                    dataPath: 'field',
                    label: t('panels.accounts.filterLabels.advanced.field'),
                    options: advancedSearchItems.map((item) => ({
                        label: item.label,
                        value: item.value,
                        operatorOptions: item.operatorOptions,
                    })),
                    dataTest: 'fields-select',
                },
                operator: {
                    dataPath: 'operator',
                    label: t('panels.accounts.filterLabels.advanced.operator'),
                    dataTest: 'operator-select',
                },
                value: {
                    dataPath: 'value',
                    label: t('panels.accounts.filterLabels.advanced.value'),
                    dataTest: 'value-input',
                },
            },
            dataTest: 'advanced-search',
        });
        let accountSystemRoleFilter = undefined;
        if (showAccountSystemRoleFilter === 'quickFilter') {
            accountSystemRoleFilter = builder.quickFilterMultiSelect({
                dataPath: 'systemRole',
                label: t('panels.accounts.columnLabels.systemRole'),
                values: Object.entries(filteredSystemRoleMap).map(([name, value]) => ({ value: name, label: value.label })),
                widget: 'form.multiSelect',
                sx: { maxWidth: { xs: undefined, sm: 280 } },
                dataTest: 'system-role-input',
            });
        }
        let accountStatusFilter = undefined;
        if (showAccountStatusFilter === 'quickFilter') {
            accountStatusFilter = builder.quickFilterMultiSelect({
                dataPath: 'systemStatus',
                label: t('panels.accounts.columnLabels.systemStatus'),
                values: systemStatuses
                    .filter((key) => key !== 'deleted')
                    .map((value) => ({ value, label: t(`panels.accounts.status.${value}`) })),
                widget: 'form.multiSelect',
                sx: { maxWidth: { xs: undefined, sm: 280 } },
                dataTest: 'system-status-input',
            });
        }
        let organizationStatusFilter = undefined;
        if (showOrganizationStatusFilter === 'quickFilter' && staticOrganizationId) {
            organizationStatusFilter = builder.quickFilterMultiSelect({
                dataPath: 'orgStatusInStaticOrg',
                label: t('panels.accounts.filterLabels.organizationStatus'),
                values: Object.entries(orgStatusMap).map(([name, value]) => ({
                    value: staticOrganizationId + '_' + name,
                    label: value.label,
                })),
                widget: 'form.multiSelect',
                sx: { maxWidth: { xs: undefined, sm: 280 } },
                dataTest: 'organization-role-input',
            });
        }
        let organizationRoleFilter = undefined;
        if (showOrganizationRoleFilter === 'quickFilter' && staticOrganizationId) {
            organizationRoleFilter = builder.quickFilterMultiSelect({
                dataPath: 'orgRoleInStaticOrg',
                label: t('panels.accounts.filterLabels.organizationRole'),
                values: Object.entries(orgRoleMap).map(([name, value]) => ({
                    value: staticOrganizationId + '_' + name,
                    label: value.label,
                })),
                widget: 'form.multiSelect',
                sx: { maxWidth: { xs: undefined, sm: 280 } },
                dataTest: 'organization-status-input',
            });
        }
        builder.allFiltersPanel({
            key: '!search', // This key can be used by other products to extend/modify account filters.
            children: [advancedSearchFilter],
        });
        builder.allFiltersPanel({
            key: '!identityInformation', // This key can be used by other products to extend/modify account filters.
            label: t('panels.accounts.filterLabels.identityInformation'),
            children: [
                builder.allFiltersWidget({
                    type: 'form.text',
                    key: 'firstName',
                    label: t('panels.accounts.filterLabels.firstName'),
                    emptyAsNull: true,
                }),
                builder.allFiltersWidget({
                    type: 'form.text',
                    key: 'lastName',
                    label: t('panels.accounts.filterLabels.lastName'),
                }),
                builder.allFiltersWidget({
                    type: 'form.email',
                    key: 'email',
                    label: t('panels.accounts.filterLabels.email'),
                }),
                builder.allFiltersWidget({
                    type: 'form.phone',
                    key: 'phoneNumber',
                    label: t('panels.accounts.filterLabels.phoneNumber'),
                }),
                builder.allFiltersWidget({
                    type: 'form.text',
                    key: 'pagerNumber',
                    label: t('panels.accounts.filterLabels.pagerNumber'),
                    validate: {
                        custom: (value) => {
                            if (value == null || /^\d{10}$/.test(value)) {
                                return;
                            }
                            return t('panels.accounts.filterValidationError.pagerNumber');
                        },
                    },
                }),
            ],
            style: {
                columns: 2,
            },
        });
        builder.allFiltersPanel({
            key: '!orgMembership', // This key can be used by other products to extend/modify account filters.
            label: t('panels.accounts.filterLabels.orgMembership'),
            children: [
                builder.allFiltersWidget({
                    type: 'contactManagement.organizationSharing',
                    key: 'orgAssociation',
                    showSelectAllChildren: true,
                    includeByOrgPermission: ['manage-members'],
                }, (filterData) => __awaiter(this, void 0, void 0, function* () {
                    if (filterData.orgAssociation) {
                        const organizationsMap = yield getOrganizationMap(filterData.orgAssociation);
                        return filterData.orgAssociation.map((staticOrganizationId) => {
                            var _a, _b;
                            return ({
                                label: (_b = (_a = organizationsMap[staticOrganizationId]) === null || _a === void 0 ? void 0 : _a.columns.name) !== null && _b !== void 0 ? _b : 'Deleted Organization',
                                value: staticOrganizationId,
                                groupId: staticOrganizationId,
                                onRemoveChip: (prevFilterData, value) => {
                                    const newFilterData = JSON.parse(JSON.stringify(prevFilterData));
                                    if (newFilterData.orgAssociation) {
                                        newFilterData.orgAssociation = newFilterData.orgAssociation.filter((v) => v !== value);
                                        if (newFilterData.orgAssociation.length === 0) {
                                            delete newFilterData.orgAssociation;
                                        }
                                    }
                                    return newFilterData;
                                },
                            });
                        });
                    }
                    else {
                        return [];
                    }
                })),
                ...(showOrganizationStatusFilter
                    ? showOrganizationStatusFilter === 'quickFilter' && organizationStatusFilter && staticOrganizationId
                        ? [organizationStatusFilter]
                        : [
                            builder.allFiltersWidget({
                                type: 'form.multiSelect',
                                key: 'orgStatusInStaticOrg',
                                label: t('panels.accounts.filterLabels.organizationStatus'),
                                values: Object.entries(orgStatusMap).map(([name, value]) => ({
                                    value: staticOrganizationId + '_' + name,
                                    label: value.label,
                                })),
                            }),
                        ]
                    : [
                        builder.allFiltersWidget({
                            type: 'form.checkboxList',
                            key: 'orgStatusSet',
                            label: t('panels.accounts.filterLabels.organizationStatus'),
                            values: Object.entries(orgStatusMap)
                                .filter(([, status]) => !requireOrgRoleInStatusFilter || (requireOrgRoleInStatusFilter && status.requireOrgRole))
                                .map(([name, value]) => ({ value: name, label: value.label })),
                            style: { layout: '2-columns-stretch' },
                        }),
                    ]),
                {
                    type: 'form.divider',
                },
                ...(showOrganizationRoleFilter
                    ? showOrganizationRoleFilter === 'quickFilter' && organizationRoleFilter && staticOrganizationId
                        ? [organizationRoleFilter]
                        : [
                            builder.allFiltersWidget({
                                type: 'form.multiSelect',
                                key: 'orgRoleInStaticOrg',
                                label: t('panels.accounts.filterLabels.organizationRole'),
                                values: Object.entries(orgRoleMap).map(([name, value]) => ({
                                    value: staticOrganizationId + '_' + name,
                                    label: value.label,
                                })),
                            }),
                        ]
                    : [
                        builder.allFiltersWidget({
                            type: 'form.checkboxList',
                            key: 'orgRoleSet',
                            label: t('panels.accounts.filterLabels.organizationRole'),
                            values: Object.entries(orgRoleMap).map(([name, value]) => ({ value: name, label: value.label })),
                            style: { layout: '2-columns-stretch' },
                        }),
                    ]),
            ],
        });
        builder.allFiltersPanel({
            key: '!groupMembership', // This key can be used by other products to extend/modify account filters.
            label: t('panels.accounts.filterLabels.groupMembership'),
            children: [
                builder.allFiltersWidget({
                    label: t('panels.accounts.filterLabels.group'),
                    type: 'form.multiSelect',
                    key: 'groupMembership',
                    valueProvider: {
                        type: 'contactManagement.group',
                    },
                }),
            ],
        });
        builder.allFiltersPanel({
            key: '!accountInformation', // This key can be used by other products to extend/modify account filters.
            label: t('panels.accounts.filterLabels.accountInformation'),
            children: [
                builder.allFiltersWidget({
                    type: 'form.checkboxList',
                    key: 'contactIssues',
                    label: t('panels.accounts.filterLabels.contactIssues'),
                    values: [
                        { value: 'hasBouncedEmails', label: t('panels.accounts.filterLabels.emailAddressBounced') },
                        {
                            value: useConsentlessTexting ? 'hasCanceledPhoneNumbers' : 'hasUnconfirmedPhoneNumbers',
                            label: t('panels.accounts.filterLabels.consentForTextUnavailable'),
                        },
                    ],
                    style: { layout: '2-columns-stretch' },
                }),
                {
                    type: 'form.divider',
                },
                builder.allFiltersWidget({
                    type: 'form.checkboxList',
                    key: 'contactMethodSet',
                    label: t('panels.accounts.filterLabels.contactMethod'),
                    values: [
                        { value: 'voice', label: t('panels.accounts.filterLabels.voice') },
                        { value: 'text', label: t('panels.accounts.filterLabels.text') },
                        { value: 'fax', label: t('panels.accounts.filterLabels.fax') },
                        { value: 'email', label: t('panels.accounts.filterLabels.email') },
                        { value: 'pager', label: t('panels.accounts.filterLabels.pager') },
                    ],
                    style: { layout: '2-columns-stretch' },
                }),
                ...(showAccountSystemRoleFilter
                    ? showAccountSystemRoleFilter === 'quickFilter' && accountSystemRoleFilter
                        ? [accountSystemRoleFilter]
                        : [
                            builder.allFiltersWidget({
                                type: 'form.multiSelect',
                                key: 'systemRole',
                                label: t('panels.accounts.columnLabels.systemRole'),
                                values: Object.entries(filteredSystemRoleMap).map(([name, value]) => ({
                                    value: name,
                                    label: value.label,
                                })),
                            }),
                        ]
                    : []),
                ...(showAccountSystemRoleFilter && showAccountStatusFilter
                    ? [
                        {
                            type: 'form.divider',
                        },
                    ]
                    : []),
                ...(showAccountStatusFilter
                    ? showAccountStatusFilter === 'quickFilter' && accountStatusFilter
                        ? [accountStatusFilter]
                        : [
                            builder.allFiltersWidget({
                                type: 'form.multiSelect',
                                key: 'systemStatus',
                                label: t('panels.accounts.columnLabels.systemStatus'),
                                values: systemStatuses
                                    .filter((key) => key !== 'deleted')
                                    .map((value) => ({ value, label: t(`panels.accounts.status.${value}`) })),
                            }),
                        ]
                    : []),
            ],
        });
        builder.allFiltersPanel({
            key: '!organizationAffiliation', // This key can be used by other products to extend/modify account filters.
            label: t('panels.accounts.filterLabels.organizationAffiliation'),
            children: [
                builder.allFiltersWidget({
                    type: 'form.checkbox',
                    key: 'unaffiliatedOnly',
                    label: t('panels.accounts.filterLabels.unaffiliatedOnly'),
                }),
            ],
        });
        builder.allFiltersPanel({
            key: '!lastLoginDate', // This key can be used by other products to extend/modify account filters.
            label: t('panels.accounts.filterLabels.lastLoginDate'),
            children: [
                builder.allFiltersWidget({
                    type: 'form.datePicker',
                    key: 'loginAfter',
                    label: t('common.after'),
                }),
                builder.allFiltersWidget({
                    type: 'form.datePicker',
                    key: 'loginBefore',
                    label: t('common.before'),
                }),
            ],
            style: {
                columns: 2,
            },
        });
        builder.allFiltersPanel({
            key: '!lastUpdatedDate', // This key can be used by other products to extend/modify account filters.
            label: t('panels.accounts.filterLabels.lastUpdatedDate'),
            children: [
                builder.allFiltersWidget({
                    type: 'form.datePicker',
                    key: 'updatedAfter',
                    label: t('common.after'),
                }),
                builder.allFiltersWidget({
                    type: 'form.datePicker',
                    key: 'updatedBefore',
                    label: t('common.before'),
                }),
            ],
            style: {
                columns: 2,
            },
        });
        builder.allFiltersPanel({
            key: '!registrationDate', // This key can be used by other products to extend/modify account filters.
            label: t('panels.accounts.filterLabels.registrationDate'),
            children: [
                {
                    type: 'form.panel',
                    children: [
                        builder.allFiltersWidget({
                            type: 'form.datePicker',
                            key: 'registeredAfter',
                            label: t('common.after'),
                        }),
                        builder.allFiltersWidget({
                            type: 'form.datePicker',
                            key: 'registeredBefore',
                            label: t('common.before'),
                        }),
                    ],
                    style: {
                        layout: 'row',
                    },
                },
            ],
            style: {
                columns: 1,
            },
        });
        const dynamicFiltersPanel = buildDynamicFilters(builder, t('panels.accounts.customFilters'), contactManagement.dynamicColumns);
        if (dynamicFiltersPanel) {
            builder.allFiltersPanel(dynamicFiltersPanel);
        }
        extendFilters === null || extendFilters === void 0 ? void 0 : extendFilters(builder);
    }, 
    // TODO Andrius Sukys - change from useMemoOne to React.useMemo resulted in this eslint warning
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        t,
        showAccountSystemRoleFilter,
        showAccountStatusFilter,
        showOrganizationRoleFilter,
        staticOrganizationId,
        showOrganizationStatusFilter,
        orgRoleMap,
        orgStatusMap,
        systemRoleMap,
        contactManagement,
        extendFilters,
        staticOrganizationName,
        requireOrgRoleInStatusFilter,
        advancedSearchItems,
    ]);
}
